import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { Toaster } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import { UserAuthProvider } from "@/context/UserAuthContext";
import SmoothScroll from "@/components/SmoothScroll";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { trackPageView } from "@/lib/analytics";
import Home from "@/pages/Home";
import ProjectDetail from "@/pages/ProjectDetail";
import Quiz from "@/pages/Quiz";
import Login from "@/pages/Login";
import Admin from "@/pages/Admin";
import Insights from "@/pages/Insights";
import InsightDetail from "@/pages/InsightDetail";
import AuthCallback from "@/pages/AuthCallback";
import Account from "@/pages/Account";
import Book from "@/pages/Book";
import Maintenance from "@/pages/Maintenance";
import { isMaintenanceMode } from "@/lib/maintenance";

const SiteLayout = ({ children, footer = true }) => (
  <>
    <Navbar />
    {children}
    {footer && <Footer />}
  </>
);

function AppRoutes() {
  const location = useLocation();

  useEffect(() => {
    trackPageView(location.pathname + location.search);
  }, [location.pathname, location.search]);

  // CRITICAL: detect Emergent OAuth callback synchronously during render.
  // useEffect would fire after ProtectedRoute could kick in.
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  // Under-construction mode: live hosts show the holding screen; /login and
  // /admin stay reachable so the studio can keep managing content.
  const staffPath = location.pathname.startsWith("/login") || location.pathname.startsWith("/admin");
  if (isMaintenanceMode() && !staffPath) {
    return <Maintenance />;
  }

  return (
    <Routes location={location}>
      <Route path="/" element={<SiteLayout><Home /></SiteLayout>} />
      <Route path="/projects/:id" element={<SiteLayout><ProjectDetail /></SiteLayout>} />
      <Route path="/insights" element={<SiteLayout><Insights /></SiteLayout>} />
      <Route path="/insights/:slug" element={<SiteLayout><InsightDetail /></SiteLayout>} />
      <Route path="/quiz" element={<SiteLayout footer={false}><Quiz /></SiteLayout>} />
      <Route path="/book" element={<SiteLayout footer={false}><Book /></SiteLayout>} />
      <Route path="/account" element={<SiteLayout footer={false}><Account /></SiteLayout>} />
      <Route path="/login" element={<Login />} />
      <Route path="/admin" element={<Admin />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <div className="noise-overlay" />
      <BrowserRouter>
        <AuthProvider>
          <UserAuthProvider>
            <SmoothScroll>
              <AppRoutes />
            </SmoothScroll>
          </UserAuthProvider>
        </AuthProvider>
      </BrowserRouter>
      <Toaster position="top-center" richColors />
    </div>
  );
}

export default App;
